import { Box, Typography } from '@mui/material';
import { useListAgentsQuery } from '../../api/agents.api';
import { AgentCard } from '../../components/AgentCard';
import { ContentState } from '../../components/ContentState';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { cardGridSx, contentWidthSx, pageGutterSx, sectionSpacingSx } from '../../theme';

/**
 * `/toolkit` — every agent the toolkit knows about.
 *
 * **It sits in the Toolkit section and not under the SDLC**, because an agent is
 * a thing in its own right that happens to be linked to methods, and the Toolkit
 * is where the things that are not the catalog live. It wraps itself in the page
 * gutter for the same reason the Luna Telemetry page does: `SdlcLayout`, which
 * supplies it for the catalog, is not above it.
 *
 * **Empty is a state, not an error.** Before an environment's first load the
 * library holds nothing, which is a fact about the environment rather than a fault.
 */
export const AgentLibraryPage = () => {
  const { data, isLoading, error, refetch } = useListAgentsQuery();
  const agents = data ?? [];

  return (
    <Box sx={pageGutterSx}>
      <Box sx={{ ...contentWidthSx, paddingBlock: 12 }}>
        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load the agent library"
          errorNoun="agent library"
          isEmpty={agents.length === 0}
          emptyTitle="No agents yet"
          emptyMessage="Agents appear here once they have been released from the repository, and prompts once they have been approved in the Prompt Library."
          skeleton={<LoadingSkeleton layout="agents" label="Loading the agent library" />}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography
              variant="overline"
              component="p"
              sx={{ color: 'text.secondary', letterSpacing: '1px' }}
            >
              Toolkit
            </Typography>

            <Typography variant="h1" component="h1">
              Agent library
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 720 }}>
              Agents and prompts that help with the methods in the SDLC catalog. Each is versioned,
              and each is linked to the methods it supports.
            </Typography>
          </Box>

          <Box component="ul" sx={{ ...cardGridSx('card'), ...sectionSpacingSx, listStyle: 'none', margin: 0, padding: 0 }}>
            {agents.map((agent) => (
              <Box component="li" key={agent.name}>
                <AgentCard agent={agent} />
              </Box>
            ))}
          </Box>
        </ContentState>
      </Box>
    </Box>
  );
};
